import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ListboxComponent from './ListBoxComponent';
import debounce from 'lodash/debounce';
import Autocomplete from '@material-ui/lab/Autocomplete';
import cn from 'classnames';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  makeStyles,
  withStyles,
} from '@material-ui/core';
import Tooltip, { InfoPropTypes } from 'components/Tooltip';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import PopUp from 'components/Operational/PopUp/PopUp';
import Label from 'components/Label';

export const DROPDOWN_ITEMS_MAX = 6;
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const WithBackground = withStyles(theme => ({
  root: {
    color: theme.palette.text.primary,
  },
}))(Box);

const useStyles = makeStyles(theme => ({
  formControl: {
    display: 'flex',
  },
  autocomplete: {
    background: theme.palette.background.default,
    border: `1px solid ${theme.palette.text.secondary}`,
    color: theme.palette.text.primary,
    '&[aria-expanded="true"] [class*="MuiInputBase-inputAdornedStart"]': {
      padding: 0,
    },
    '& [class*="MuiInput-root"]': {
      width: '100%',
      flexWrap: 'unset',
      height: 30,
      color: ({ disabled }) => {
        if (typeof disabled === 'boolean' && disabled) {
          return theme.palette.secondary.light;
        } else {
          return theme.palette.text.primary;
        }
      },
      background: ({ disabled }) => {
        if (typeof disabled === 'boolean' && disabled) {
          return theme.palette.text.disabled;
        } else {
          return 'none';
        }
      },
    },

    '& [class*="MuiChip-sizeSmall"]': {
      margin: '0px',
    },
  },
  rootInput: {
    '&::placeholder': ({ disabled }) => {
      if (typeof disabled === 'boolean' && disabled) {
        return theme.palette.secondary.light;
      } else {
        return theme.palette.text.primary;
      }
    },
  },
  InputLabel: {
    display: 'flex',
  },
  clearIndicator: {
    color: theme.palette.text.primary,
  },
  popupIndicatorOpen: {
    color: theme.palette.text.primary,
  },
  popupIndicator: {
    color: theme.palette.text.primary,
  },
  popper: {
    border: `0.5px solid ${theme.palette.text.secondary}`,
  },
  startAdornment: {
    maxHeight: '60px',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    display: 'flex',
  },
  option: {
    padding: 0,
    color: theme.palette.text.primary,
    '&:hover': {
      background: theme.palette.primary.main,
      color: theme.palette.secondary.light,
    },
  },
  menuItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '6px 16px',
    width: '100%',
    '&:hover': {
      background: theme.palette.primary.main,
      color: theme.palette.secondary.light,
    },
  },
}));

const AsyncSelect = ({
  className,
  onFocus,
  name,
  disabled = false,
  label,
  value = null,
  required,
  onChange,
  error,
  info,
  shrinkLabel = true,
  placeholder = 'Select',
  noOptionsText,
  hidden,
  fetchOptions,
  onChangeCallback,
  optionLabelKey,
  displayError = true,
  fetchFilters = {},
  dataTransform,
  multiple,
  limitTags = 1,
  fixedOptions = [],
  useAsyncInFilters,
  resetWhen,
  item,
  formProps,
  setFormikFieldValues,
}) => {
  const { t } = useTranslation();
  // FIX-ME: REFACTOR THIS
  const [options, setOptions] = useState([]);
  const [from, setFrom] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [itemCount, setItemCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [displayPopup, setDisplayPopup] = useState({});
  const searchRef = useRef({ current: { value: null } });
  const originalListRef = useRef({ current: { value: [], itemCount: 0 } });
  const classes = useStyles({ disabled });

  // For hooking async with any other non async component, we can make use of this state
  useEffect(() => {
    if (value?.category && name !== 'taskType') {
      //clear the previous category options
      fetchTask({
        resetOptions: true,
        startIndex: 0,
        doNotAdd: true, // this flag will not concatinate the newly fetched data with previous
        // This is used when we change the category and need to fetch new data..
      });
    }

    if (Array.isArray(resetWhen)) {
      resetWhen.forEach(f => {
        if (typeof f.condition === 'function') {
          if (f.condition(f.value)) {
            // condition on when we want to reset the options based on filter values
            fetchTask({
              apiCall: false, // this helps to just reset the async options and not make the api call
              resetOptions: true,
              startIndex: 0,
              doNotAdd: true, // this flag will not concatinate the newly fetched data with previous
              // This is used when we change the category and need to fetch new data..
            });
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, resetWhen]);

  const fetchTask = async ({
    startIndex = 0,
    stopIndex = DROPDOWN_ITEMS_MAX,
    search,
    resetOptions,
    includeSearch,
    doNotAdd = false,
    apiCall = true,
  }) => {
    if (disabled) {
      return;
    }
    try {
      setIsLoadingMore(true);
      let finalData = [];
      let metaTotal = 0;
      let metaCount = 0;
      let metaFrom = 0;
      if (apiCall) {
        const { data } = await fetchOptions({
          ...fetchFilters,
          from: startIndex,
          limit: stopIndex,
          search,
        });
        finalData = data.data;
        metaTotal = data.meta.total;
        metaCount = data.meta.count;
        metaFrom = data.meta.from;
        if (typeof dataTransform === 'function' && data.data) {
          finalData = dataTransform({ data: data.data });
        }
      }

      if (!isEmpty(fixedOptions) && startIndex === 0 && finalData.length > 0) {
        finalData.unshift(...fixedOptions);
      }

      let opts = [];
      if (resetOptions) {
        opts = finalData;
      }
      const total = doNotAdd ? finalData : options.concat(finalData);
      const totalCount = isEmpty(fixedOptions)
        ? metaTotal
        : metaTotal + fixedOptions.length;
      opts = [...new Map(total.map(item => [item.id, item])).values()];

      setHasNextPage(metaCount + metaFrom < totalCount);
      setItemCount(totalCount);
      setOptions(opts);
      if (!includeSearch) {
        originalListRef.current = { value: opts, itemCount: totalCount };
      }
      setIsLoadingMore(false);
    } catch (e) {
      setIsLoadingMore(false);
      console.log({ e });
    }
  };

  const handleLoadMore = ({ searching, searchValue, clear }) => {
    if (searching) {
      fetchTask({
        startIndex: 0,
        search: searchValue,
        resetOptions: true,
        includeSearch: true,
      });
      return;
    }

    // get options from originalListRef if options have been fully loaded
    const { value = [], itemCount = 0 } = originalListRef.current;
    const totalCount = value ? value.length : 0;
    searchRef.current.value = null;
    setFrom(0);

    if (totalCount < itemCount) {
      if (clear) {
        fetchTask({
          startIndex: 0,
          resetOptions: true,
        });
        return;
      }
      setHasNextPage(true);
      setOptions([]);

      return;
    }

    setOptions(value);
    setHasNextPage(false);
    setItemCount(itemCount);
  };

  const handleChange = (event, selectedValue, reason) => {
    if (item?.popupRequired) {
      setDisplayPopup({ event, selectedValue, reason });
      return;
    }

    onChange(selectedValue, name, reason);
    if (typeof onChangeCallback === 'function') {
      onChangeCallback(selectedValue, value, onChange);
    }
    if (useAsyncInFilters) {
      onChangeCallback(selectedValue, name, reason);
    }
  };

  const handleInputChange = useCallback(
    debounce((event, value, reason) => {
      searchRef.current = { value };
      switch (reason) {
        case 'input':
          if (value.length >= 3 || !value) {
            handleLoadMore({ searching: true, searchValue: value });
          }
          break;

        case 'clear':
          handleLoadMore({ searching: false, clear: true });
          break;
        case 'reset':
          if (item?.popupRequired) {
            return;
          }
          handleLoadMore({ searching: false });
          break;

        default:
          break;
      }
    }, 300),
    [],
  );

  const loadMoreItems = (startIndex, stopIndex) => {
    setFrom(startIndex);

    const limit = stopIndex + 2 - startIndex;
    if (limit > 0) {
      fetchTask({
        startIndex: startIndex > 0 ? startIndex - 1 : startIndex,
        stopIndex: limit,
        search: searchRef.current.value,
        includeSearch: Boolean(searchRef.current.value),
      });
    }
  };

  const isItemLoaded = index => {
    if (!hasNextPage) {
      return true;
    }
    return Boolean(options[index]);
  };

  const onOpen = () => {
    if (hasNextPage || (itemCount < 1 && from < 1)) {
      fetchTask({ startIndex: from });
    }
  };
  const onClose = () => {
    setFrom(0); // reset the from page to 0
    setOptions([]); // reset options
    setItemCount(0);
  };
  const textErrorOrBool = typeof error === 'string' ? t(error) : error;
  const getOptionId = option =>
    typeof option === 'string' ? option : option.id;
  return (
    <>
      <FormControl
        disabled={disabled}
        className={cn(classes.formControl, className, {
          [classes.hidden]: hidden,
        })}
        required={required}
        error={!disabled && Boolean(error)}
      >
        <InputLabel
          shrink={shrinkLabel}
          htmlFor={name}
          className={disabled ? classes.disabled : classes.input}
          classes={{ root: classes.InputLabel }}
        >
          {t(label)}
          {info && <Tooltip text={info} />}
        </InputLabel>
        <Autocomplete
          multiple={multiple}
          value={value}
          classes={{
            root: classes.autocomplete,
            option: classes.option,
            popupIndicator: classes.popupIndicator,
            clearIndicator: classes.clearIndicator,
            popupIndicatorOpen: classes.popupIndicatorOpen,
            popper: classes.popper,
            input: classes.rootInput,
          }}
          disableCloseOnSelect
          blurOnSelect={!multiple}
          filterOptions={options => options}
          onOpen={onOpen}
          onClose={onClose}
          onFocus={onFocus}
          onChange={handleChange}
          onInputChange={handleInputChange}
          disabled={disabled}
          options={options || []}
          ListboxComponent={ListboxComponent}
          ListboxProps={{
            isItemLoaded,
            loadMoreItems,
            itemCount,
            ...(value &&
              options && {
                scrollToIndex: options.findIndex(item => item.id === value.id),
              }),
          }}
          label={label}
          info={info}
          name={name}
          noOptionsText={noOptionsText ? t(noOptionsText) : undefined}
          required={required}
          hidden={hidden}
          limitTags={limitTags}
          renderInput={params => {
            return (
              <Input
                placeholder={t(placeholder)}
                disableUnderline
                {...params.InputProps}
                startAdornment={
                  <div className={classes.startAdornment}>
                    {params.InputProps.startAdornment}
                  </div>
                }
                endAdornment={
                  <>
                    {isLoadingMore ? (
                      <CircularProgress color="primary" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                }
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'new-password',
                  form: {
                    autoComplete: 'off',
                  },
                }}
              />
            );
          }}
          getOptionLabel={option => {
            if (item?.popupRequired) {
              // show the last selected option string
              return formProps.values[name][optionLabelKey] || '';
            }

            return option[optionLabelKey] ? option[optionLabelKey] : '';
          }}
          getOptionSelected={(option, selected) => {
            if (item?.popupRequired) {
              // show the last selected option string
              return formProps.values[name][optionLabelKey];
            }
            return selected ? option.id === getOptionId(selected) : false;
          }}
          // getOptionSelected={(option, selected) => option.id === selected.id}
          renderOption={(option, { selected }) => {
            return multiple ? (
              <React.Fragment>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  className={classes.checkbox}
                  checked={selected}
                />
                <Label
                  icon={option?.icon ? option.icon : ''}
                  alt={'icons'}
                  className={classes.icon}
                />
                {option[optionLabelKey]}
              </React.Fragment>
            ) : (
              <WithBackground
                className={classes.menuItem}
                id={`${option[optionLabelKey]}`}
              >
                <Label
                  icon={option?.icon ? option.icon : ''}
                  alt={'icons'}
                  className={classes.icon}
                />
                {option[optionLabelKey]}
              </WithBackground>
            );
          }}
          ChipProps={{
            size: 'small',
          }}
        />
        {displayError && (
          <FormHelperText id="component-error-text">
            {textErrorOrBool}
          </FormHelperText>
        )}
      </FormControl>
      {Object.keys(displayPopup).length > 0 && (
        <PopUp
          title={item.popupMessages.popupTitle}
          close={() => setDisplayPopup({})}
          statusMessage={item.popupMessages.popupStatusMessage}
          submit={() => {
            // reset the fields you want after submitting the popup
            if (item?.resetRelatedFields) {
              setFormikFieldValues(item?.resetRelatedFields);
            }
            handleLoadMore({ searching: false });
            onChange(displayPopup.selectedValue, name, displayPopup.reason);
            // close the popup
            setDisplayPopup({});
          }}
          submitBtnText={item.popupMessages.submitBtnText}
          cancel={() => {
            // close the popup
            setDisplayPopup({});
          }}
          cancelBtnText={item.popupMessages.cancelBtnText}
        />
      )}
    </>
  );
};

AsyncSelect.propTypes = {
  disabled: PropTypes.bool,
  className: PropTypes.string,
  autoComplete: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.object,
  ]),
  onInputChange: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  name: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  valueType: PropTypes.string,
  shrinkLabel: PropTypes.bool,
  optionLabelKey: PropTypes.string,
  useSelectedValue: PropTypes.bool,
  useAsyncInFilters: PropTypes.bool,
  placeholder: PropTypes.string,
  displayError: PropTypes.bool,
  info: InfoPropTypes,
  noOptionsText: PropTypes.string,
  hidden: PropTypes.bool,
  fetchOptions: PropTypes.func,
  onChangeCallback: PropTypes.func,
  fetchFilters: PropTypes.object,
  dataTransform: PropTypes.func,
  multiple: PropTypes.bool,
  limitTags: PropTypes.number,
  fixedOptions: PropTypes.array,
  resetWhen: PropTypes.array,
  item: PropTypes.object,
  formProps: PropTypes.object,
  setFormikFieldValues: PropTypes.func,
};

export default AsyncSelect;
